/**
 * WeaveIn.ai Platform — External Referrals (TRUS-511)
 *
 * Maui LTRG: "Referrals & Care Coordination - this section should be more generic than
 * health focused. For this file, I am offering a referral to apply to a USDA Single
 * Family Disaster Grant - the deadline is 9/30 and I have offered to help them gather
 * the documents needed and get their application packet ready and submitted in time."
 *
 * So this panel tracks four things and no more: who the household was referred to, what
 * for, when it is due, and where it got to. No diagnosis, no health plan, no provider
 * matching — that is the other referrals surface, and TRUS-519 turns it off for this
 * org precisely because its clinical framing confused them.
 *
 * The deadline is the reason the panel exists rather than a note being enough: it is
 * what the case manager is actually tracking, so overdue and due-soon are called out
 * rather than buried in prose.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, FileText, Loader2, Plus, Save, Trash2, X } from 'lucide-react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  externalReferralsAPI,
  type ExternalReferral,
  type ExternalReferralInput,
  type ExternalReferralStatus,
} from '@/services/api/externalReferrals';

interface ExternalReferralsPanelProps {
  memberId: number;
  /** Hides every mutating control — used by read-only profile views. */
  readOnly?: boolean;
}

const STATUS_LABELS: Record<ExternalReferralStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  submitted: 'Submitted',
  approved: 'Approved',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
};

const STATUS_STYLES: Record<ExternalReferralStatus, string> = {
  open: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  submitted: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  denied: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  withdrawn: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const EMPTY: ExternalReferralInput = {
  agency_name: '',
  program_name: '',
  purpose: '',
  deadline: '',
  status: 'open',
};

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/** The deadline in the terms the case manager thinks in, not a raw date. */
function deadlineNote(r: ExternalReferral): { text: string; urgent: boolean } | null {
  if (!r.deadline || r.days_until_deadline == null) return null;
  const d = r.days_until_deadline;
  if (r.is_overdue) return { text: `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue`, urgent: true };
  if (d === 0) return { text: 'Due today', urgent: true };
  if (d > 0 && d <= 14) return { text: `Due in ${d} day${d === 1 ? '' : 's'}`, urgent: d <= 7 };
  return null;
}

const ExternalReferralsPanel: React.FC<ExternalReferralsPanelProps> = ({ memberId, readOnly }) => {
  const [referrals, setReferrals] = useState<ExternalReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState<ExternalReferralInput>(EMPTY);
  /** id currently being changed, so only that row shows a spinner. */
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReferrals(await externalReferralsAPI.list(memberId));
    } catch (e: any) {
      setError(getApiErrorMessage(e) || 'Could not load referrals.');
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!draft.agency_name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Empty strings are "not filled in", not values to store.
      await externalReferralsAPI.create(memberId, {
        ...draft,
        agency_name: draft.agency_name.trim(),
        program_name: draft.program_name?.trim() || undefined,
        purpose: draft.purpose?.trim() || undefined,
        deadline: draft.deadline || undefined,
      });
      setDraft(EMPTY);
      setComposerOpen(false);
      await load();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || 'Could not save the referral.');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (referral: ExternalReferral, status: ExternalReferralStatus) => {
    setBusyId(referral.id);
    setError(null);
    try {
      await externalReferralsAPI.update(referral.id, { status });
      await load();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || 'Could not update the referral.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (referral: ExternalReferral) => {
    setBusyId(referral.id);
    setError(null);
    try {
      await externalReferralsAPI.remove(referral.id);
      await load();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || 'Could not remove the referral.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Referrals to outside agencies
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {loading
              ? 'Loading referrals…'
              : `${referrals.length} referral${referrals.length === 1 ? '' : 's'} on file`}
          </p>
        </div>
        {!readOnly && !composerOpen && (
          <Button size="sm" onClick={() => setComposerOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Log a referral
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}

      {composerOpen && !readOnly && (
        <div className="rounded-lg border border-v2border bg-surface p-3 space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">Agency *</label>
              <input
                value={draft.agency_name}
                onChange={(e) => setDraft({ ...draft, agency_name: e.target.value })}
                placeholder="e.g. USDA Rural Development"
                className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Program</label>
              <input
                value={draft.program_name || ''}
                onChange={(e) => setDraft({ ...draft, program_name: e.target.value })}
                placeholder="e.g. Single Family Disaster Grant"
                className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Application deadline</label>
              <input
                type="date"
                value={draft.deadline || ''}
                onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
                className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Status</label>
              <select
                value={draft.status || 'open'}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as ExternalReferralStatus })}
                className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
              >
                {(Object.keys(STATUS_LABELS) as ExternalReferralStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">What the referral is for</label>
            <textarea
              value={draft.purpose || ''}
              onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
              rows={3}
              placeholder="What you have offered to help with, and anything the household needs to do."
              className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !draft.agency_name.trim()}>
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save referral
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setComposerOpen(false);
                setDraft(EMPTY);
              }}
            >
              <X className="w-4 h-4 mr-1.5" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading referrals…
        </div>
      ) : referrals.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p className="text-sm">No referrals to outside agencies yet.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {referrals.map((r) => {
            const note = deadlineNote(r);
            return (
              <li key={r.id} className="rounded-lg border border-v2border bg-surface px-3 py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">
                      {r.agency_name}
                      {r.program_name ? ` · ${r.program_name}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Referred {formatDate(r.referral_date)}
                      {r.deadline ? ` · due ${formatDate(r.deadline)}` : ''}
                      {r.created_by_name ? ` · ${r.created_by_name}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {note && (
                      <span
                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                          note.urgent
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        }`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {note.text}
                      </span>
                    )}
                    <Badge className={STATUS_STYLES[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                  </div>
                </div>

                {r.purpose && (
                  <p className="mt-1.5 text-sm text-ink whitespace-pre-wrap leading-relaxed break-words">
                    {r.purpose}
                  </p>
                )}

                {/* The packet. These are real member documents, filed on the Documents
                    tab and pointed at this referral — not a private pile of files. */}
                {r.documents.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {r.documents.map((d) => (
                      <span
                        key={d.doc_record_id}
                        title={d.doc_type_name || undefined}
                        className="inline-flex items-center gap-1 rounded border border-v2border px-2 py-0.5 text-xs text-ink-500 max-w-[240px]"
                      >
                        <FileText className="w-3 h-3 shrink-0" />
                        <span className="truncate">{d.filename || `Document #${d.doc_record_id}`}</span>
                        {d.role === 'application' && (
                          <span className="text-[10px] uppercase tracking-wide text-v2accent">app</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {!readOnly && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select
                      value={r.status}
                      disabled={busyId === r.id}
                      onChange={(e) => void changeStatus(r, e.target.value as ExternalReferralStatus)}
                      aria-label="Referral status"
                      className="h-7 rounded-md border border-v2border bg-surface px-2 text-xs"
                    >
                      {(Object.keys(STATUS_LABELS) as ExternalReferralStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => void remove(r)}
                      className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-600"
                    >
                      {busyId === r.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                      Remove
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ExternalReferralsPanel;
